import { fetchTopicsForChapter } from "../apis/CourseApis";
import { fetchChapterSubmissions } from "../apis/SubmissionApis";
import { storageGetJson } from "./storage";

const COURSE_PROGRESS_KEY = "codearena_course_progress";

const getTopicId = (t) => String(t?.id || t?._id || "");

/**
 * Gets stored course progress from localStorage (synchronous).
 * Returns { completedTopics, lastAccessedAt, chapters } or empty object.
 */
export const getCourseProgress = (courseId) => {
  if (!courseId) return { completedTopics: 0 };
  const allProgress = storageGetJson(COURSE_PROGRESS_KEY, {});
  return allProgress[courseId] || { completedTopics: 0 };
};

/**
 * Saves course progress to localStorage.
 */
export const saveCourseProgress = (courseId, progressData) => {
  if (!courseId || typeof window === "undefined") return;
  const allProgress = storageGetJson(COURSE_PROGRESS_KEY, {});
  allProgress[courseId] = {
    ...allProgress[courseId],
    ...progressData,
    lastAccessedAt: Date.now(),
  };
  window.localStorage.setItem(COURSE_PROGRESS_KEY, JSON.stringify(allProgress));
};

/**
 * Increments completed topics count for a course.
 */
export const markTopicCompleted = (courseId, topicId) => {
  if (!courseId || !topicId) return;
  const current = getCourseProgress(courseId);
  const completedSet = new Set(current.completedTopicIds || []);
  
  if (!completedSet.has(topicId)) {
    completedSet.add(topicId);
    saveCourseProgress(courseId, {
      completedTopics: completedSet.size,
      completedTopicIds: Array.from(completedSet),
    });
  }
};

const computeProgressFromTopicsAndSubmissions = ({ topics, submissions }) => {
  const bestScoreByTopicId = {};

  for (const sub of submissions) {
    const topicId = String(sub?.topicId || sub?.topic || sub?.topicID || "");
    if (!topicId) continue;
    const score = Number.isFinite(sub?.score) ? sub.score : 0;
    bestScoreByTopicId[topicId] = Math.max(bestScoreByTopicId[topicId] || 0, score);
  }

  // Only MCQ/Coding are measurable today.
  const measurable = topics.filter((t) => {
    const type = String(t?.type || "").toLowerCase();
    return type === "mcq" || type === "coding";
  });

  if (measurable.length === 0) {
    // No measurable topics → treat as 0 to avoid unlocking by accident.
    return 0;
  }

  let completed = 0;
  for (const t of measurable) {
    const topicId = getTopicId(t);
    const totalPoints = Number.isFinite(t?.points) ? t.points : 0;
    const best = bestScoreByTopicId[topicId] || 0;

    // If points exist, completion means bestScore >= points.
    // If points are missing/0, consider it completed only if there was a submission.
    const attempted = Boolean(bestScoreByTopicId[topicId] != null);
    const isCompleted = totalPoints > 0 ? best >= totalPoints : attempted;

    if (isCompleted) completed += 1;
  }

  return Math.round((completed / measurable.length) * 100);
};

/**
 * Computes chapter completion percent (0-100) for a logged-in user.
 * Uses existing endpoints: topics list + chapter submissions.
 */
export const computeChapterCompletionPercent = async ({ chapterId, token }) => {
  if (!chapterId || !token) return 0;

  const [topicsRes, subsRes] = await Promise.all([
    fetchTopicsForChapter(chapterId),
    fetchChapterSubmissions(chapterId, token),
  ]);

  const topics = Array.isArray(topicsRes?.topics)
    ? topicsRes.topics
    : Array.isArray(topicsRes)
      ? topicsRes
      : [];

  const submissions = Array.isArray(subsRes?.submissions)
    ? subsRes.submissions
    : Array.isArray(subsRes)
      ? subsRes
      : [];

  return computeProgressFromTopicsAndSubmissions({ topics, submissions });
};
